import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Input,
  Button,
  Box,
  Heading,
  FormLabel,
  Select,
  Avatar,
  Switch,
  Text,
  VStack,
  HStack,
  IconButton,
  useToast,
  Flex,
  SimpleGrid,
  Divider,
  Badge,
  InputGroup,
  InputLeftElement,
  Tooltip,
  FormControl,
  Icon,
  Spinner,
} from "@chakra-ui/react";
import { DeleteIcon, EditIcon } from "@chakra-ui/icons";
import {
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";

import { useAuth } from "../../auth/AuthContext";
import { db, storage } from "../../configs/firebase";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import {
  Building2,
  Palette,
  Phone,
  Landmark,
  Users,
  Sun,
  Moon,
  ShieldCheck,
  Lock,
  LogIn,
  Search,
} from "lucide-react";
import { WarehouseItem } from "../warehouses/WarehouseCard";
import WarehouseManager from "../warehouses/WarehouseManager";
import GlobalSpinner from "../CustomReusables/GlobalSpinner";
import {
  normalizeUserRole,
  USER_ROLES,
  UserRole,
} from "../Profile/constants/userRoles";
import {
  canManageOwnMembers,
  getAssignableMemberRoles,
} from "../Profile/utils/rolePermissions";

interface UserMember {
  id: string;
  name: string;
  email: string;
  role?: string;
  photoURL?: string;
  [key: string]: any;
}

interface UserSearchDropdownProps {
  onSelect: (user: UserMember) => void | Promise<void>;
}

const rawApiUrl = "http://localhost:5000/api/v1";

const API_BASE_URL = String(rawApiUrl).replace(/\/$/, "");

const cardsBorderRadii = "8px";

const UserSearchDropdown = ({ onSelect }: UserSearchDropdownProps) => {
  const { theme } = useCustomTheme();
  const toast = useToast();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<UserMember[]>([]);
  const [searching, setSearching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    const trimmedQuery = query.trim();

    if (trimmedQuery.length < 2) {
      setResults([]);
      setIsOpen(false);
      setSearching(false);
      return;
    }

    const controller = new AbortController();

    const timer = window.setTimeout(async () => {
      setSearching(true);

      try {
        const currentUser = getAuth().currentUser;

        if (!currentUser) {
          throw new Error("You must be signed in to search users.");
        }

        const token = await currentUser.getIdToken();
        const response = await fetch(
          `${API_BASE_URL}/user-directory?search=${encodeURIComponent(
            trimmedQuery,
          )}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
            },
            signal: controller.signal,
          },
        );

        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            payload?.message ||
              payload?.error ||
              "Users could not be searched.",
          );
        }

        const users = Array.isArray(payload?.users)
          ? payload.users
          : Array.isArray(payload?.data)
            ? payload.data
            : [];

        const normalizedUsers: UserMember[] = users
          .map((candidate: Record<string, unknown>) => {
            const id = String(candidate.uid || candidate.id || "");
            const name = String(
              candidate.displayName || candidate.name || candidate.email || id,
            );

            return {
              ...candidate,
              id,
              name,
              email: String(candidate.email || ""),
              ...(candidate.photoURL
                ? { photoURL: String(candidate.photoURL) }
                : {}),
              role: candidate.role ? String(candidate.role) : USER_ROLES.GUEST,
            } as UserMember;
          })
          .filter((candidate: UserMember) => Boolean(candidate.id));

        setResults(normalizedUsers);
        setIsOpen(true);
      } catch (error) {
        if ((error as Error).name === "AbortError") {
          return;
        }

        console.error("User search failed:", error);
        setResults([]);
        setIsOpen(false);

        toast({
          title: "User search failed",
          description:
            error instanceof Error ? error.message : "Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [query, toast]);

  const handleSelect = async (selectedUser: UserMember) => {
    await onSelect(selectedUser);
    setQuery("");
    setResults([]);
    setIsOpen(false);
  };

  return (
    <Box ref={containerRef} position="relative" width="100%">
      <InputGroup>
        <InputLeftElement pointerEvents="none">
          {searching ? <Spinner size="sm" /> : <Search size={16} />}
        </InputLeftElement>

        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onFocus={() => {
            if (results.length > 0) {
              setIsOpen(true);
            }
          }}
          placeholder="Search by name, email, or user ID"
          bg={theme.inputBg}
          color={theme.text}
          borderColor={theme.inputBorder}
          _placeholder={{ color: theme.inputPlaceholder }}
          _hover={{ borderColor: theme.borderStrong }}
          _focus={{
            borderColor: theme.inputBorderFocus,
            boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
          }}
        />
      </InputGroup>

      {isOpen && (
        <VStack
          position="absolute"
          top="calc(100% + 8px)"
          left={0}
          right={0}
          zIndex={30}
          spacing={0}
          align="stretch"
          maxH="320px"
          overflowY="auto"
          bg={theme.surface}
          border="1px solid"
          borderColor={theme.border}
          borderRadius={cardsBorderRadii}
          boxShadow={theme.shadowMd}
        >
          {results.length === 0 ? (
            <Text p={4} color={theme.textMuted}>
              No matching users found.
            </Text>
          ) : (
            results.map((result) => (
              <HStack
                key={result.id}
                p={3}
                cursor="pointer"
                _hover={{ bg: theme.hover }}
                onClick={() => handleSelect(result)}
                borderBottom="1px solid"
                borderColor={theme.border}
                _last={{ borderBottom: "none" }}
              >
                <Avatar size="sm" src={result.photoURL} name={result.name} />

                <Box minW={0}>
                  <Text fontWeight="700" color={theme.text} noOfLines={1}>
                    {result.name}
                  </Text>

                  <Text fontSize="sm" color={theme.textMuted} noOfLines={1}>
                    {result.email || result.id}
                  </Text>
                </Box>
              </HStack>
            ))
          )}
        </VStack>
      )}
    </Box>
  );
};

const SettingsPage = () => {
  const { user } = useAuth();
  const { theme, themeName, toggleTheme } = useCustomTheme();
  const toast = useToast();
  const navigate = useNavigate();

  const pageBg = theme.background;
  const cardBg = theme.surface;
  const softBg = theme.surfaceAlt;
  const borderColor = theme.border;
  const borderStrong = theme.borderStrong;
  const subtleText = theme.textMuted;
  const tertiaryText = theme.textSubtle;
  const inputBg = theme.inputBg;
  const inputBorder = theme.inputBorder;
  const focusBorder = theme.inputBorderFocus;

  const isDark = theme.name === "dark";

  const [form, setForm] = useState({
    name: "",
    fontColor: "#000000",
    businessName: "",
    businessAddress: "",
    logoFile: null as File | null,
    logoURL: "",
    phoneNumber: "",
    accountName: "",
    accountNumber: "",
    accountBranch: "",
    accountBank: "",
  });

  const [members, setMembers] = useState<UserMember[]>([]);
  const [selectedMember, setSelectedMember] = useState<UserMember | null>(null);
  const [addingMember, setAddingMember] = useState(false);
  const [warehouses, setWarehouses] = useState<WarehouseItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [themeSaving, setThemeSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [viewerRole, setViewerRole] = useState<string>(USER_ROLES.GUEST);
  const normalizedViewerRole = normalizeUserRole(viewerRole);
  const canManageMembers = canManageOwnMembers(normalizedViewerRole);
  const assignableMemberRoles = getAssignableMemberRoles(normalizedViewerRole);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const fetchSettingsAndMembers = async () => {
      setLoading(true);
      try {
        const docSnap = await getDoc(doc(db, "users", user.uid));
        if (docSnap.exists()) {
          const data = docSnap.data();
          setViewerRole(normalizeUserRole(data.role));

          setForm((prev) => ({
            ...prev,
            name: data.name || "",
            fontColor: data.fontColor || "#000000",
            businessName: data.businessName || "",
            businessAddress: data.businessAddress || "",
            logoURL: data.logoURL || "",
            phoneNumber: data.phoneNumber || "",
            accountName: data.accountName || "",
            accountNumber: data.accountNumber || "",
            accountBranch: data.accountBranch || "",
            accountBank: data.accountBank || "",
          }));

          if (Array.isArray(data.members)) {
            setMembers(data.members);
          } else {
            setMembers([]);
          }

          if (Array.isArray(data.warehouses)) {
            setWarehouses(data.warehouses);
          } else {
            setWarehouses([]);
          }
        }
      } catch (error) {
        console.error("Failed to fetch settings:", error);
        toast({
          title: "Failed to load settings",
          status: "error",
          duration: 3000,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSettingsAndMembers();
  }, [user, toast]);

  const memberCount = members.length;
  const warehouseCount = warehouses.length;

  const roleBadgeColor = useMemo(() => {
    if (normalizedViewerRole === USER_ROLES.SUPER_ADMIN) return "red";
    if (normalizedViewerRole === USER_ROLES.ADMIN) return "purple";
    if (normalizedViewerRole === USER_ROLES.SHOP) return "teal";
    return "gray";
  }, [normalizedViewerRole]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleThemeToggle = async () => {
    if (!user) return;

    setThemeSaving(true);
    try {
      await toggleTheme();
      toast({
        title: `Theme changed to ${themeName === "dark" ? "light" : "dark"}`,
        status: "success",
        duration: 1800,
      });
    } catch (error) {
      console.error("Theme update failed:", error);
      toast({
        title: "Failed to update theme",
        status: "error",
        duration: 3000,
      });
    } finally {
      setThemeSaving(false);
    }
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!user) return;
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);

    try {
      const storageRef = ref(storage, `logos/${user.uid}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        "state_changed",
        () => {},
        (error) => {
          console.error("Upload error:", error);
          toast({
            title: "Logo upload failed",
            status: "error",
            duration: 3000,
          });
          setUploading(false);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

          await updateDoc(doc(db, "users", user.uid), {
            logoURL: downloadURL,
          });

          const userSnap = await getDoc(doc(db, "users", user.uid));
          const businessId = userSnap.exists()
            ? userSnap.data().businessId
            : null;

          if (businessId) {
            await updateDoc(doc(db, "businesses", businessId), {
              logoURL: downloadURL,
            });
          }

          setForm((prev) => ({ ...prev, logoURL: downloadURL }));

          toast({
            title: "Logo updated successfully",
            status: "success",
            duration: 2500,
          });
          setUploading(false);
        },
      );
    } catch (error) {
      console.error("Upload failed:", error);
      toast({
        title: "Upload failed",
        status: "error",
        duration: 3000,
      });
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!user) return;

    setSaving(true);
    try {
      const payload: Record<string, any> = {
        name: form.name,
        fontColor: form.fontColor,
        businessName: form.businessName,
        businessAddress: form.businessAddress,
        logoURL: form.logoURL,
        phoneNumber: form.phoneNumber,
        accountName: form.accountName,
        accountNumber: form.accountNumber,
        accountBranch: form.accountBranch,
        accountBank: form.accountBank,
      };

      await updateDoc(doc(db, "users", user.uid), payload);

      toast({
        title: "Settings updated",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (err) {
      console.error(err);
      toast({
        title: "Failed to update settings",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleMemberSelected = (member: UserMember) => {
    if (members.some((existingMember) => existingMember.id === member.id)) {
      toast({
        title: "Member already added",
        description: "This user is already part of your business team.",
        status: "info",
        duration: 2800,
        isClosable: true,
      });
      setSelectedMember(null);
      return;
    }

    const defaultRole = assignableMemberRoles.includes(USER_ROLES.STAFF)
      ? USER_ROLES.STAFF
      : assignableMemberRoles[0] || USER_ROLES.GUEST;

    setSelectedMember({
      ...member,
      id: String(member.id || "").trim(),
      name: String(member.name || member.email || member.id || "Unnamed user"),
      email: String(member.email || ""),
      role: defaultRole,
    });
  };

  const handleAddMember = async () => {
    if (!user || !canManageMembers || !selectedMember?.id) {
      toast({
        title: "Search for and select a member first",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    if (members.some((member) => member.id === selectedMember.id)) {
      toast({
        title: "Member already added",
        status: "info",
        duration: 2500,
        isClosable: true,
      });
      setSelectedMember(null);
      return;
    }

    const selectedRole = normalizeUserRole(selectedMember.role);
    const defaultRole = assignableMemberRoles.includes(USER_ROLES.STAFF)
      ? USER_ROLES.STAFF
      : assignableMemberRoles[0] || USER_ROLES.GUEST;

    const memberToAdd: UserMember = {
      id: String(selectedMember.id || "").trim(),
      name: String(
        selectedMember.name ||
          selectedMember.email ||
          selectedMember.id ||
          "Unnamed user",
      ).trim(),
      email: String(selectedMember.email || "").trim(),
      role: assignableMemberRoles.includes(selectedRole)
        ? selectedRole
        : defaultRole,
      createdAt: selectedMember.createdAt || new Date().toISOString(),
      ...(selectedMember.photoURL
        ? { photoURL: String(selectedMember.photoURL).trim() }
        : {}),
    };

    setAddingMember(true);

    try {
      const selectedMemberSnapshot = await getDoc(
        doc(db, "users", selectedMember.id),
      );

      if (!selectedMemberSnapshot.exists()) {
        setSelectedMember(null);
        throw new Error("The selected user no longer exists.");
      }

      await updateDoc(doc(db, "users", user.uid), {
        members: arrayUnion(memberToAdd),
      });

      setMembers((prev) => [...prev, memberToAdd]);
      setSelectedMember(null);

      toast({
        title: "Member added",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      console.error(err);
      toast({
        title: "Error adding member",
        description: err instanceof Error ? err.message : "Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (member: UserMember) => {
    if (!user || !canManageMembers) return;
    try {
      await updateDoc(doc(db, "users", user.uid), {
        members: arrayRemove(member),
      });
      setMembers((prev) => prev.filter((m) => m.id !== member.id));
      toast({
        title: "Member removed",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      console.error(err);
      toast({
        title: "Failed to remove member",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const handleMemberRoleChange = async (
    memberId: string,
    nextRole: UserRole,
  ) => {
    if (!user || !canManageMembers) return;

    if (!assignableMemberRoles.includes(nextRole)) {
      toast({
        title: "Role not allowed",
        description: "You cannot assign this member role.",
        status: "error",
        duration: 3000,
      });
      return;
    }

    try {
      const userRef = doc(db, "users", user.uid);
      const userSnapshot = await getDoc(userRef);

      if (!userSnapshot.exists()) {
        throw new Error("Account was not found.");
      }

      const existingMembers = Array.isArray(userSnapshot.data().members)
        ? (userSnapshot.data().members as UserMember[])
        : [];

      const updatedMembers = existingMembers.map((member) =>
        member.id === memberId
          ? {
              ...member,
              role: nextRole,
              updatedAt: new Date().toISOString(),
            }
          : member,
      );

      await updateDoc(userRef, {
        members: updatedMembers,
        updatedAt: new Date(),
      });

      setMembers(updatedMembers);
      toast({
        title: "Member role updated",
        status: "success",
        duration: 2000,
      });
    } catch (error) {
      console.error("Failed to update member role:", error);
      toast({
        title: "Failed to update member role",
        status: "error",
        duration: 3000,
      });
    }
  };

  if (loading) {
    return (
      <Box p={6} bg={pageBg} minH="100%" color={theme.text}>
        <Flex justify="center" align="center" minH="240px">
          <GlobalSpinner />
        </Flex>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box
        p={{ base: 4, md: 6 }}
        bg={pageBg}
        minH="100%"
        color={theme.text}
        borderRadius={cardsBorderRadii}
      >
        <Box mx="auto">
          <Box
            bg={cardBg}
            border="1px solid"
            borderColor={borderColor}
            borderRadius={cardsBorderRadii}
            p={{ base: 4, md: 6 }}
            boxShadow={theme.shadowMd}
            mb={6}
            position="relative"
            overflow="hidden"
          >
            <Flex
              justify="space-between"
              align={{ base: "start", md: "center" }}
              direction={{ base: "column", md: "row" }}
              gap={4}
            >
              <Box>
                <HStack spacing={2} mb={2} flexWrap="wrap">
                  <Badge
                    px={3}
                    py={1}
                    borderRadius={cardsBorderRadii}
                    bg={
                      isDark ? "rgba(39,183,255,0.14)" : "rgba(39,183,255,0.08)"
                    }
                    color={isDark ? "blue.200" : "blue.700"}
                    fontWeight="700"
                  >
                    Settings
                  </Badge>

                  <Badge
                    px={3}
                    py={1}
                    borderRadius={cardsBorderRadii}
                    bg={
                      isDark ? "rgba(176,92,255,0.14)" : "rgba(176,92,255,0.08)"
                    }
                    color={isDark ? "purple.200" : "purple.700"}
                    fontWeight="700"
                  >
                    Account
                  </Badge>

                  <Badge
                    px={3}
                    py={1}
                    borderRadius={cardsBorderRadii}
                    bg={
                      isDark ? "rgba(239,68,68,0.16)" : "rgba(239,68,68,0.10)"
                    }
                    color={isDark ? "red.200" : "red.700"}
                    fontWeight="700"
                  >
                    Login required
                  </Badge>
                </HStack>

                <Heading size="lg" color={theme.text}>
                  Sign in to manage your settings
                </Heading>

                <Text color={subtleText} mt={2} fontWeight="500" maxW="760px">
                  Access your business details, branding, banking information,
                  team members, warehouses, and app appearance from one secure
                  place.
                </Text>
              </Box>

              <Box
                border="1px solid"
                borderColor={borderStrong}
                borderRadius={cardsBorderRadii}
                p={3}
                bg={softBg}
                minW={{ base: "100%", md: "260px" }}
                boxShadow={theme.shadowSm}
              >
                <HStack justify="space-between">
                  <VStack align="start" spacing={0}>
                    <Text fontWeight="semibold" color={theme.text}>
                      Secure settings
                    </Text>
                    <Text fontSize="xs" color={tertiaryText}>
                      Login protects your account
                    </Text>
                  </VStack>

                  <Flex
                    w="34px"
                    h="34px"
                    borderRadius={cardsBorderRadii}
                    align="center"
                    justify="center"
                    bg={
                      isDark ? "rgba(239,68,68,0.12)" : "rgba(239,68,68,0.08)"
                    }
                    border={`1px solid ${isDark ? "rgba(239,68,68,0.22)" : "rgba(239,68,68,0.16)"}`}
                  >
                    <Icon
                      as={Lock}
                      boxSize={4}
                      color={isDark ? "red.200" : "red.600"}
                    />
                  </Flex>
                </HStack>
              </Box>
            </Flex>
          </Box>

          <Box
            borderRadius={cardsBorderRadii}
            bg={cardBg}
            boxShadow={theme.shadowSm}
            border={`1px solid ${borderColor}`}
            p={{ base: 5, md: 6 }}
          >
            <VStack spacing={4} align="start">
              <Flex
                w="56px"
                h="56px"
                borderRadius={cardsBorderRadii}
                align="center"
                justify="center"
                bg={isDark ? "rgba(239,68,68,0.12)" : "rgba(239,68,68,0.08)"}
                border={`1px solid ${isDark ? "rgba(239,68,68,0.22)" : "rgba(239,68,68,0.16)"}`}
              >
                <Icon
                  as={Lock}
                  boxSize={6}
                  color={isDark ? "red.200" : "red.600"}
                />
              </Flex>

              <Box>
                <Text color={theme.text} fontWeight="800" fontSize="lg">
                  You need to be logged in
                </Text>
                <Text color={subtleText} mt={1}>
                  Sign in to update your business profile, manage team members,
                  configure warehouses, upload branding, and update payout
                  details.
                </Text>
              </Box>

              <HStack spacing={3} pt={1} flexWrap="wrap">
                <Button
                  leftIcon={<LogIn size={16} />}
                  color={theme.textOnPrimary}
                  bgImage={theme.gradients?.primaryButton || theme.primary}
                  _hover={{
                    bgImage:
                      theme.gradients?.primaryButtonHover || theme.primaryHover,
                    transform: "translateY(-1px)",
                  }}
                  _active={{ transform: "translateY(0)" }}
                  borderRadius={cardsBorderRadii}
                  boxShadow={theme.shadowSm}
                  transition="all 0.2s ease"
                  onClick={() => navigate("/login")}
                >
                  Sign In
                </Button>

                <Button
                  variant="outline"
                  borderColor={borderStrong}
                  color={theme.text}
                  bg={cardBg}
                  _hover={{ bg: theme.hover }}
                  borderRadius={cardsBorderRadii}
                  onClick={() => navigate("/dashboard")}
                >
                  Go back
                </Button>
              </HStack>
            </VStack>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      p={{ base: 4, md: 6 }}
      bg={pageBg}
      minH="100%"
      color={theme.text}
      borderRadius={cardsBorderRadii}
    >
      <Box mx="auto">
        <Box
          bg={cardBg}
          border="1px solid"
          borderColor={borderColor}
          borderRadius={cardsBorderRadii}
          p={{ base: 4, md: 6 }}
          boxShadow={theme.shadowMd}
          mb={6}
        >
          <Flex
            justify="space-between"
            align={{ base: "start", md: "center" }}
            direction={{ base: "column", md: "row" }}
            gap={4}
          >
            <Box>
              <Heading size="lg" color={theme.text}>
                Settings
              </Heading>
              <Text color={subtleText} mt={1}>
                Manage business details, branding, banking info, team members,
                warehouses, and app appearance.
              </Text>
            </Box>

            <Box
              border="1px solid"
              borderColor={borderStrong}
              borderRadius={cardsBorderRadii}
              p={3}
              bg={softBg}
              minW={{ base: "100%", md: "260px" }}
            >
              <HStack justify="space-between">
                <VStack align="start" spacing={0}>
                  <Text fontWeight="semibold" color={theme.text}>
                    App Theme
                  </Text>
                  <Text fontSize="xs" color={tertiaryText}>
                    Changes the whole app
                  </Text>
                </VStack>
                <HStack spacing={2} color={theme.text}>
                  {themeName === "dark" ? (
                    <Moon size={16} />
                  ) : (
                    <Sun size={16} />
                  )}
                  <Switch
                    id="theme-switch"
                    isChecked={themeName === "dark"}
                    onChange={handleThemeToggle}
                    isDisabled={themeSaving}
                    colorScheme="teal"
                  />
                </HStack>
              </HStack>
            </Box>
          </Flex>
        </Box>

        <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6}>
          <VStack spacing={6} align="stretch">
            <Box
              bg={cardBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius={cardsBorderRadii}
              p={{ base: 4, md: 5 }}
              boxShadow={theme.shadowSm}
            >
              <HStack mb={4} color={theme.text}>
                <Building2 size={18} />
                <Text fontWeight="bold" fontSize="lg">
                  Business Identity
                </Text>
              </HStack>

              <Flex
                direction={{ base: "column", md: "row" }}
                gap={5}
                align={{ base: "flex-start", md: "center" }}
              >
                <Box position="relative" w="100px" h="100px">
                  <Avatar
                    src={form.logoURL}
                    name={form.businessName || form.name}
                    size="xl"
                    boxSize="100px"
                    border="2px solid"
                    borderColor={theme.primary}
                    bg={softBg}
                  />
                  <Tooltip
                    label={uploading ? "Uploading..." : "Change logo"}
                    hasArrow
                  >
                    <IconButton
                      icon={<EditIcon />}
                      size="sm"
                      position="absolute"
                      bottom={0}
                      right={0}
                      aria-label="Edit logo"
                      borderRadius={cardsBorderRadii}
                      onClick={() => fileInputRef.current?.click()}
                      zIndex={1}
                      bg={theme.primary}
                      color={theme.textOnPrimary}
                      _hover={{ bg: theme.primaryHover }}
                      isDisabled={uploading}
                    />
                  </Tooltip>

                  <Input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    display="none"
                  />

                  {uploading && (
                    <Flex
                      position="absolute"
                      inset={0}
                      align="center"
                      justify="center"
                      bg={theme.overlay}
                      borderRadius={cardsBorderRadii}
                    >
                      <GlobalSpinner />
                    </Flex>
                  )}
                </Box>

                <VStack align="stretch" spacing={4} flex="1" w="full">
                  <FormControl>
                    <FormLabel color={subtleText}>Name</FormLabel>
                    <Input
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Name"
                      bg={inputBg}
                      color={theme.text}
                      borderColor={inputBorder}
                      _placeholder={{ color: theme.inputPlaceholder }}
                      _hover={{ borderColor: borderStrong }}
                      _focus={{
                        borderColor: focusBorder,
                        boxShadow: `0 0 0 1px ${focusBorder}`,
                      }}
                    />
                  </FormControl>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl>
                      <FormLabel color={subtleText}>Account Role</FormLabel>
                      <Input
                        value={normalizedViewerRole}
                        isReadOnly
                        textTransform="capitalize"
                        bg={softBg}
                        color={theme.text}
                        borderColor={borderColor}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel color={subtleText}>Font Color</FormLabel>
                      <InputGroup>
                        <InputLeftElement
                          pointerEvents="none"
                          color={subtleText}
                        >
                          <Palette size={16} />
                        </InputLeftElement>
                        <Input
                          type="color"
                          name="fontColor"
                          value={form.fontColor}
                          onChange={handleChange}
                          pl="2.5rem"
                          h="40px"
                          p={1}
                          bg={inputBg}
                          borderColor={inputBorder}
                          _hover={{ borderColor: borderStrong }}
                          _focus={{
                            borderColor: focusBorder,
                            boxShadow: `0 0 0 1px ${focusBorder}`,
                          }}
                        />
                      </InputGroup>
                    </FormControl>
                  </SimpleGrid>
                </VStack>
              </Flex>

              <HStack mt={4} spacing={2} flexWrap="wrap">
                <Badge
                  colorScheme={roleBadgeColor}
                  borderRadius={cardsBorderRadii}
                  textTransform="capitalize"
                >
                  {normalizedViewerRole}
                </Badge>
                {canManageMembers && (
                  <Badge colorScheme="purple" borderRadius={cardsBorderRadii}>
                    <HStack spacing={1}>
                      <ShieldCheck size={12} />
                      <Text>Can manage business members</Text>
                    </HStack>
                  </Badge>
                )}
              </HStack>
            </Box>

            <Box
              bg={cardBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius={cardsBorderRadii}
              p={{ base: 4, md: 5 }}
              boxShadow={theme.shadowSm}
            >
              <HStack mb={4} color={theme.text}>
                <Phone size={18} />
                <Text fontWeight="bold" fontSize="lg">
                  Business Contact
                </Text>
              </HStack>

              <VStack spacing={4} align="stretch">
                <Input
                  name="businessName"
                  value={form.businessName}
                  onChange={handleChange}
                  placeholder="Business Name"
                  bg={inputBg}
                  color={theme.text}
                  borderColor={inputBorder}
                  _placeholder={{ color: theme.inputPlaceholder }}
                  _hover={{ borderColor: borderStrong }}
                  _focus={{
                    borderColor: focusBorder,
                    boxShadow: `0 0 0 1px ${focusBorder}`,
                  }}
                />
                <Input
                  name="businessAddress"
                  value={form.businessAddress}
                  onChange={handleChange}
                  placeholder="Business Address"
                  bg={inputBg}
                  color={theme.text}
                  borderColor={inputBorder}
                  _placeholder={{ color: theme.inputPlaceholder }}
                  _hover={{ borderColor: borderStrong }}
                  _focus={{
                    borderColor: focusBorder,
                    boxShadow: `0 0 0 1px ${focusBorder}`,
                  }}
                />
                <Input
                  name="phoneNumber"
                  value={form.phoneNumber}
                  onChange={handleChange}
                  placeholder="Phone Number"
                  bg={inputBg}
                  color={theme.text}
                  borderColor={inputBorder}
                  _placeholder={{ color: theme.inputPlaceholder }}
                  _hover={{ borderColor: borderStrong }}
                  _focus={{
                    borderColor: focusBorder,
                    boxShadow: `0 0 0 1px ${focusBorder}`,
                  }}
                />
              </VStack>
            </Box>

            <Box
              bg={cardBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius={cardsBorderRadii}
              p={{ base: 4, md: 5 }}
              boxShadow={theme.shadowSm}
            >
              <HStack mb={4} color={theme.text}>
                <Landmark size={18} />
                <Text fontWeight="bold" fontSize="lg">
                  Bank Account Details
                </Text>
              </HStack>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Input
                  name="accountName"
                  value={form.accountName}
                  onChange={handleChange}
                  placeholder="Account Name"
                  bg={inputBg}
                  color={theme.text}
                  borderColor={inputBorder}
                  _placeholder={{ color: theme.inputPlaceholder }}
                  _hover={{ borderColor: borderStrong }}
                  _focus={{
                    borderColor: focusBorder,
                    boxShadow: `0 0 0 1px ${focusBorder}`,
                  }}
                />
                <Input
                  name="accountBank"
                  value={form.accountBank}
                  onChange={handleChange}
                  placeholder="Bank Name"
                  bg={inputBg}
                  color={theme.text}
                  borderColor={inputBorder}
                  _placeholder={{ color: theme.inputPlaceholder }}
                  _hover={{ borderColor: borderStrong }}
                  _focus={{
                    borderColor: focusBorder,
                    boxShadow: `0 0 0 1px ${focusBorder}`,
                  }}
                />
                <Input
                  name="accountNumber"
                  value={form.accountNumber}
                  onChange={handleChange}
                  placeholder="Account Number"
                  bg={inputBg}
                  color={theme.text}
                  borderColor={inputBorder}
                  _placeholder={{ color: theme.inputPlaceholder }}
                  _hover={{ borderColor: borderStrong }}
                  _focus={{
                    borderColor: focusBorder,
                    boxShadow: `0 0 0 1px ${focusBorder}`,
                  }}
                />
                <Input
                  name="accountBranch"
                  value={form.accountBranch}
                  onChange={handleChange}
                  placeholder="Branch"
                  bg={inputBg}
                  color={theme.text}
                  borderColor={inputBorder}
                  _placeholder={{ color: theme.inputPlaceholder }}
                  _hover={{ borderColor: borderStrong }}
                  _focus={{
                    borderColor: focusBorder,
                    boxShadow: `0 0 0 1px ${focusBorder}`,
                  }}
                />
              </SimpleGrid>

              <Divider my={5} borderColor={theme.divider} />

              <Flex
                justify="space-between"
                align={{ base: "start", md: "center" }}
                direction={{ base: "column", md: "row" }}
                gap={3}
              >
                <Text fontSize="sm" color={subtleText}>
                  Save business, branding, and banking settings in one update.
                </Text>
                <Button
                  bg={theme.primary}
                  color={theme.textOnPrimary}
                  _hover={{ bg: theme.primaryHover }}
                  onClick={handleSubmit}
                  isLoading={saving}
                  loadingText="Saving"
                  minW="170px"
                >
                  Update Settings
                </Button>
              </Flex>
            </Box>
          </VStack>

          <VStack spacing={6} align="stretch">
            <Box
              bg={cardBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius={cardsBorderRadii}
              p={{ base: 4, md: 5 }}
              boxShadow={theme.shadowSm}
            >
              <Text fontWeight="bold" fontSize="lg" mb={4} color={theme.text}>
                Settings Overview
              </Text>

              <SimpleGrid columns={2} spacing={3}>
                <OverviewTile
                  label="Role"
                  value={normalizedViewerRole}
                  capitalize
                  theme={theme}
                />
                <OverviewTile
                  label="Theme"
                  value={themeName}
                  capitalize
                  theme={theme}
                />
                <OverviewTile
                  label="Members"
                  value={memberCount}
                  theme={theme}
                />
                <OverviewTile
                  label="Warehouses"
                  value={warehouseCount}
                  theme={theme}
                />
                <OverviewTile
                  label="Logo"
                  value={form.logoURL ? "Uploaded" : "Not set"}
                  theme={theme}
                />
              </SimpleGrid>
            </Box>

            <Box
              bg={cardBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius={cardsBorderRadii}
              p={{ base: 4, md: 5 }}
              boxShadow={theme.shadowSm}
            >
              <HStack mb={4} color={theme.text}>
                <Users size={18} />
                <Text fontWeight="bold" fontSize="lg">
                  Business Members
                </Text>
                <Badge
                  borderRadius={cardsBorderRadii}
                  bg={theme.primarySoft}
                  color={theme.primary}
                >
                  {memberCount}
                </Badge>
              </HStack>

              <VStack
                spacing={3}
                align="stretch"
                mb={4}
                maxH="320px"
                overflowY="auto"
              >
                {members.length === 0 && (
                  <Box
                    p={4}
                    border="1px dashed"
                    borderColor={borderColor}
                    borderRadius={cardsBorderRadii}
                    bg={softBg}
                  >
                    <Text color={subtleText}>No members yet.</Text>
                  </Box>
                )}

                {members.map((m) => (
                  <HStack
                    key={m.id}
                    justify="space-between"
                    p={3}
                    border="1px solid"
                    borderColor={borderColor}
                    borderRadius={cardsBorderRadii}
                    bg={softBg}
                  >
                    <HStack minW={0}>
                      <Avatar size="sm" name={m.name} src={m.photoURL} />
                      <Box minW={0}>
                        <Text
                          fontWeight="bold"
                          noOfLines={1}
                          color={theme.text}
                        >
                          {m.name}
                        </Text>
                        <Text fontSize="sm" color={subtleText} noOfLines={1}>
                          {m.email}
                        </Text>
                      </Box>
                    </HStack>

                    <HStack flexShrink={0}>
                      {canManageMembers ? (
                        <Select
                          size="sm"
                          value={normalizeUserRole(m.role)}
                          onChange={(event) =>
                            handleMemberRoleChange(
                              m.id,
                              event.target.value as UserRole,
                            )
                          }
                          maxW="130px"
                          bg={inputBg}
                          color={theme.text}
                          borderColor={inputBorder}
                        >
                          {assignableMemberRoles.map((role) => (
                            <option key={role} value={role}>
                              {role}
                            </option>
                          ))}
                        </Select>
                      ) : (
                        <Badge
                          textTransform="capitalize"
                          borderRadius={cardsBorderRadii}
                          colorScheme={
                            normalizeUserRole(m.role) === USER_ROLES.ADMIN
                              ? "purple"
                              : normalizeUserRole(m.role) === USER_ROLES.SHOP
                                ? "teal"
                                : "gray"
                          }
                        >
                          {normalizeUserRole(m.role)}
                        </Badge>
                      )}

                      {canManageMembers && (
                        <IconButton
                          icon={<DeleteIcon />}
                          aria-label="Remove member"
                          onClick={() => handleRemoveMember(m)}
                          size="sm"
                          colorScheme="red"
                          variant="outline"
                        />
                      )}
                    </HStack>
                  </HStack>
                ))}
              </VStack>

              <Divider mb={4} borderColor={theme.divider} />

              {canManageMembers ? (
                <>
                  <Text fontWeight="semibold" mb={2} color={theme.text}>
                    Add Member
                  </Text>
                  <Text fontSize="sm" color={subtleText} mb={3}>
                    Type a name, email address, or user ID. Select the user to
                    prefill their details, choose the member role, and then
                    save.
                  </Text>

                  <UserSearchDropdown onSelect={handleMemberSelected} />

                  {selectedMember && (
                    <Box
                      mt={4}
                      p={4}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius={cardsBorderRadii}
                      bg={softBg}
                    >
                      <Flex
                        gap={3}
                        align={{ base: "flex-start", md: "center" }}
                        direction={{ base: "column", md: "row" }}
                      >
                        <HStack flex="1" minW={0}>
                          <Avatar
                            size="md"
                            src={selectedMember.photoURL}
                            name={selectedMember.name}
                          />

                          <Box minW={0}>
                            <Text
                              fontWeight="800"
                              color={theme.text}
                              noOfLines={1}
                            >
                              {selectedMember.name}
                            </Text>
                            <Text
                              fontSize="sm"
                              color={subtleText}
                              noOfLines={1}
                            >
                              {selectedMember.email || selectedMember.id}
                            </Text>
                            <Text
                              fontSize="xs"
                              color={tertiaryText}
                              noOfLines={1}
                            >
                              {selectedMember.id}
                            </Text>
                          </Box>
                        </HStack>

                        <FormControl maxW={{ base: "100%", md: "220px" }}>
                          <FormLabel color={subtleText}>Member Role</FormLabel>
                          <Select
                            value={normalizeUserRole(
                              selectedMember.role || USER_ROLES.STAFF,
                            )}
                            onChange={(event) =>
                              setSelectedMember((current) =>
                                current
                                  ? {
                                      ...current,
                                      role: event.target.value as UserRole,
                                    }
                                  : current,
                              )
                            }
                            bg={inputBg}
                            color={theme.text}
                            borderColor={inputBorder}
                          >
                            {assignableMemberRoles.map((role: UserRole) => (
                              <option key={role} value={role}>
                                {role}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                      </Flex>

                      <HStack mt={4} justify="flex-end">
                        <Button
                          variant="ghost"
                          onClick={() => setSelectedMember(null)}
                          isDisabled={addingMember}
                        >
                          Cancel
                        </Button>
                        <Button
                          colorScheme="blue"
                          onClick={handleAddMember}
                          isLoading={addingMember}
                        >
                          Add Member
                        </Button>
                      </HStack>
                    </Box>
                  )}
                </>
              ) : (
                <Text fontSize="sm" color={subtleText}>
                  Only Admin and Super Admin accounts can add, remove, or change
                  business member roles.
                </Text>
              )}
            </Box>

            <WarehouseManager
              warehouses={warehouses}
              onWarehousesUpdated={setWarehouses}
            />
          </VStack>
        </SimpleGrid>
      </Box>
    </Box>
  );
};

function OverviewTile({
  label,
  value,
  theme,
  capitalize = false,
}: {
  label: string;
  value: React.ReactNode;
  theme: any;
  capitalize?: boolean;
}) {
  return (
    <Box
      p={3}
      borderRadius={cardsBorderRadii}
      bg={theme.surfaceAlt}
      border="1px solid"
      borderColor={theme.border}
    >
      <Text fontSize="xs" color={theme.textSubtle}>
        {label}
      </Text>
      <Text
        fontWeight="bold"
        color={theme.text}
        textTransform={capitalize ? "capitalize" : undefined}
      >
        {value}
      </Text>
    </Box>
  );
}

export default SettingsPage;
